/**
 * ItemsReport — o que foi vendido, item por item, com margem.
 *
 * Substitui o card "Serviços mais vendidos", que agrupava por `quotes.title` e
 * contava DOCUMENTOS. Aqui a agregação é real (`quote_items` por `product_id` e
 * por `item_type`) e vive em `@/lib/itemsReport` — aqui só há apresentação.
 *
 * Portões: **plano** (`reports_advanced`, PRO). Sem portão de papel: a RLS de
 * `quote_items` é SELECT para qualquer membro da empresa (≠ `finance_entries`,
 * owner/admin-only), então um `member` recebe as linhas e deve ver o relatório.
 *
 * ⚠️ Três avisos que a tela é OBRIGADA a dar, senão o número parece completo
 * quando não é: linhas sem custo (margem inflada), linhas de despesa cobrável
 * (repasse, não venda) e leitura truncada.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button3D } from '@/components/ui/button-3d';
import {
  Crown,
  Package,
  Percent,
  Receipt,
  ShoppingCart,
  Users,
  WifiOff,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatBRL, formatBRLShort } from '@/lib/financeLabels';
import {
  AwaitingData,
  BlurOverlay,
  ListTail,
  WarningCard,
} from '@/components/reports/ReportGates';
import {
  ADHOC_ITEM_BADGE,
  ITEMS_REVENUE_NOTE,
  type ItemAggregate,
  type ItemsReport as ItemsReportData,
} from '@/lib/itemsReport';
import { formatQty } from '@/lib/stockReport';
import { listLimitFor, type ReportViewMode } from '@/components/reports/ReportViewToggle';
import { ReportViewToggle } from '@/components/reports/ReportViewToggle';
import { moneyAxisTick } from '@/components/reports/axisFormat';
import { plural } from '@/lib/plural';

/** Quantos itens o RESUMO mostra. O modo Detalhado mostra a lista inteira. */
const SUMMARY_LIMIT = 8;

/** Quantas barras o gráfico mostra (a lista abaixo tem o resto). */
const CHART_ITEMS = 6;

/**
 * Largura do eixo de CATEGORIA (os nomes), em px. MEDIDA em 360px: um rótulo
 * real de 12 caracteres a `fontSize: 9` ocupa 75–85px ("MANUTENÇÃO PR…" = 81px,
 * o pior caso realista testado). 98px dá a folga sem comer a área das barras.
 */
const CATEGORY_AXIS_WIDTH = 98;

/** Onde o nome do item é cortado para caber no eixo. Ver {@link CATEGORY_AXIS_WIDTH}. */
const LABEL_MAX_CHARS = 12;

// ===== amostra para o usuário grátis (será borrada) =====

const SAMPLE_ITEMS = [
  { key: 's1', name: 'Instalação elétrica', revenue: 8400, quantity: 12, marginPercent: 46 },
  { key: 's2', name: 'Cabo flexível 2,5mm', revenue: 3100, quantity: 320, marginPercent: 28 },
  { key: 's3', name: 'Visita técnica', revenue: 1800, quantity: 9, marginPercent: 61 },
  { key: 's4', name: 'Disjuntor 25A', revenue: 950, quantity: 24, marginPercent: 33 },
];

/** A amostra no formato do gráfico (mesmos campos, sem colidir com o recharts). */
const SAMPLE_CHART = SAMPLE_ITEMS.map((s) => ({
  label: shortLabel(s.name),
  fullName: s.name,
  revenue: s.revenue,
  barColor: '#10b981',
}));

interface ItemsReportProps {
  report: ItemsReportData;
  /** `true` quando a Venda Rápida foi excluída (recorte próprio na aba Orçamentos). */
  pdvExcludedCount?: number;
  truncated?: boolean;
  loading?: boolean;
  loadFailed?: boolean;
  isOnline?: boolean;
  viewMode: ReportViewMode;
  onViewModeChange: (mode: ReportViewMode) => void;
  /** `reports_advanced.allowed`. */
  allowed: boolean;
  onUnlock: () => void;
}

export function ItemsReport({
  report,
  pdvExcludedCount = 0,
  truncated = false,
  loading = false,
  loadFailed = false,
  isOnline = true,
  viewMode,
  onViewModeChange,
  allowed,
  onUnlock,
}: ItemsReportProps) {
  const limit = listLimitFor(viewMode, SUMMARY_LIMIT);
  const shownItems = useMemo(() => report.items.slice(0, limit), [report.items, limit]);
  const shownClients = useMemo(() => report.clients.slice(0, limit), [report.clients, limit]);

  /**
   * Dado do gráfico SEM os campos que colidem com props do recharts. Ele
   * espalha os campos da entrada nas props do label/tooltip: `name`, `value`,
   * `fill`, `index` e `color` são dele, e um `percent` nosso em 0–100 produziu
   * "5556%" na pizza do Relatório de Pagamentos (#42). Aqui só vão `label`
   * (eixo) e `revenue` (valor); a cor viaja como `barColor` e é aplicada por
   * `<Cell>`.
   */
  const chartData = useMemo(
    () =>
      report.items.slice(0, CHART_ITEMS).map((item) => ({
        label: shortLabel(item.name),
        fullName: item.name,
        revenue: item.revenue,
        barColor: item.kind === 'product' ? '#0ea5e9' : '#10b981',
      })),
    [report.items],
  );

  const Gate = ({ children }: { children: React.ReactNode }) => (
    <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
      {children}
    </BlurOverlay>
  );

  const hasItems = report.items.length > 0;

  return (
    <div className="space-y-6">
      {/* De onde saem os números — antes de qualquer número. */}
      <Card className="bg-muted/30">
        <CardContent className="p-3">
          <p className="text-[11px] leading-snug text-muted-foreground">
            Itens de <span className="font-medium">orçamentos aprovados ou concluídos</span>, pela
            data de criação (a mesma base da aba Orçamentos). As vendas no balcão não entram
            {pdvExcludedCount > 0 ? ` — ${pdvExcludedCount} no período` : ''}; elas têm recorte
            próprio na aba Orçamentos.
          </p>
        </CardContent>
      </Card>

      {truncated && allowed && (
        <WarningCard>
          O período tem orçamentos demais e a leitura foi limitada. Os números abaixo estão
          incompletos — escolha um período menor.
        </WarningCard>
      )}

      {loadFailed && allowed && (
        <Card className="bg-muted/30">
          <CardContent className="flex items-start gap-2 p-3">
            <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-snug text-muted-foreground">
              {isOnline
                ? 'Não foi possível ler os itens do período. Os números abaixo estão zerados — tente de novo.'
                : 'Sem conexão: os itens do período precisam de internet. Os números abaixo estão zerados.'}
            </p>
          </CardContent>
        </Card>
      )}

      {allowed && (
        <ReportViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          hint={
            viewMode === 'detailed'
              ? `${report.items.length} ${report.items.length === 1 ? 'item' : 'itens'} · lista completa`
              : `${report.items.length} ${report.items.length === 1 ? 'item' : 'itens'} no período`
          }
        />
      )}

      {/* ===== KPIs ===== */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCard
          icon={<ShoppingCart className="h-3.5 w-3.5 text-emerald-600" />}
          tint="bg-emerald-100 dark:bg-emerald-950/30"
          label="Receita dos itens"
          value={allowed ? formatBRLShort(report.totalRevenue) : 'R$ 14,2K'}
          valueClass="text-emerald-600"
          hint={plural(allowed ? report.totalLines : 42, 'linha', 'linhas')}
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<Percent className="h-3.5 w-3.5 text-purple-600" />}
          tint="bg-purple-100 dark:bg-purple-950/30"
          label="Margem"
          value={
            allowed
              ? report.totalMarginPercent == null
                ? '—'
                : `${report.totalMarginPercent.toFixed(0)}%`
              : '42%'
          }
          valueClass="text-purple-600"
          hint={
            allowed
              ? report.totalMargin == null
                ? 'sem custo'
                : formatBRLShort(report.totalMargin)
              : 'R$ 6,0K'
          }
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<Package className="h-3.5 w-3.5 text-blue-600" />}
          tint="bg-blue-100 dark:bg-blue-950/30"
          label="Documentos"
          value={allowed ? String(report.documentCount) : '18'}
          valueClass="text-blue-600"
          hint="com itens"
          allowed={allowed}
          onUnlock={onUnlock}
        />
      </div>

      {/* Sem custo informado a margem aparece MELHOR do que é. Dizer quantas
          linhas ficaram fora é obrigatório (no silent caps). */}
      {allowed && report.missingCostLines > 0 && (
        <WarningCard>
          {report.missingCostLines}{' '}
          {report.missingCostLines === 1
            ? 'linha está sem custo informado e ficou fora da margem'
            : 'linhas estão sem custo informado e ficaram fora da margem'}
          . A margem acima considera só{' '}
          <span className="font-medium">{formatBRL(report.totalCostedRevenue)}</span> de receita —
          informe o custo no catálogo para a conta ficar completa.
        </WarningCard>
      )}

      {allowed && report.passthroughLines > 0 && (
        <Card className="bg-muted/30">
          <CardContent className="flex items-start gap-2 p-3">
            <Receipt className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-snug text-muted-foreground">
              {report.passthroughLines}{' '}
              {report.passthroughLines === 1
                ? 'linha de despesa cobrável'
                : 'linhas de despesa cobrável'}{' '}
              ({formatBRL(report.passthroughRevenue)}) ficaram de fora: repasse ao cliente não é
              venda nem tem custo próprio.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ===== Gráfico: os mais faturados ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            Itens mais faturados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && !hasItems ? (
              <AwaitingData
                hint={
                  loading
                    ? 'Carregando itens…'
                    : 'Nenhum item vendido no período selecionado'
                }
              />
            ) : (
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  {/*
                    BARRAS HORIZONTAIS de propósito, e isto foi MEDIDO, não
                    estimado (armadilha #51). Na vertical, o eixo X tem ~200px
                    de área de plotagem numa tela de 360px: com 6 itens sobram
                    33px por categoria, e um nome de item — que é TEXTO LIVRE,
                    "Instalação elétrica", "Cabo flexível 2,5mm" — mede 33–40px
                    já abreviado em 8 caracteres. Ou seja: colidiria sempre, e
                    com `interval={0}` os rótulos ficariam sobrepostos (sem ele,
                    o recharts esconde alguns e sobra barra anônima — os dois
                    resultados ruins). Na horizontal o nome vai para o eixo de
                    CATEGORIA, onde há 98px de largura própria: um rótulo real
                    de 12 caracteres mede 75–85px e cabe com folga.
                  */}
                  <BarChart
                    layout="vertical"
                    data={allowed ? chartData : SAMPLE_CHART}
                    /* right: 22 — MEDIDO. Com 12, o último tick do eixo de
                       valor ("R$ 10.0K") passava 1px da borda do SVG e saía
                       clipado em 360px; é a mesma armadilha #43 do "R$"
                       cortado, e igualmente invisível em `tsc`/jsdom. */
                    margin={{ top: 4, right: 22, left: 0, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10 }}
                      tickFormatter={moneyAxisTick}
                      className="text-muted-foreground"
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={CATEGORY_AXIS_WIDTH}
                      interval={0}
                      tick={{ fontSize: 9 }}
                      className="text-muted-foreground"
                    />
                    <Tooltip
                      formatter={(value: number) => [formatBRL(value), 'Receita']}
                      labelFormatter={(_label, payload) =>
                        String(payload?.[0]?.payload?.fullName ?? '')
                      }
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                      {(allowed ? chartData : SAMPLE_CHART).map((entry: any, index) => (
                        <Cell key={`cell-${index}`} fill={entry.barColor ?? '#10b981'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* ===== Serviço × produto ===== */}
      {allowed && report.byKind.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Serviços × produtos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {report.byKind.map((k) => (
              <div
                key={k.kind}
                className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
              >
                <div className="min-w-0">
                  <p className="text-sm">{k.label}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {k.lines} {k.lines === 1 ? 'linha' : 'linhas'} · {formatQty(k.quantity)} de
                    quantidade
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold">{formatBRLShort(k.revenue)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ===== Lista de itens ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            {viewMode === 'detailed' ? 'Todos os itens' : 'Itens do período'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed ? (
              hasItems ? (
                <div className="space-y-2">
                  {shownItems.map((item, index) => (
                    <ItemRow key={item.key} item={item} index={index} />
                  ))}
                  <ListTail total={report.items.length} shown={shownItems.length} noun="item" />
                </div>
              ) : (
                <AwaitingData hint="Nenhum item vendido no período selecionado" />
              )
            ) : (
              <div className="space-y-2">
                {SAMPLE_ITEMS.map((s) => (
                  <div
                    key={s.key}
                    className="flex items-center justify-between border-b py-2 last:border-0"
                  >
                    <span className="text-sm">{s.name}</span>
                    <span className="text-sm">{formatBRLShort(s.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* ===== Margem por cliente ===== */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Margem por cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed ? (
              report.clients.length > 0 ? (
                <div className="space-y-2">
                  {shownClients.map((client, index) => (
                    <div
                      key={`${client.clientId ?? 'none'}-${client.name}`}
                      className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="w-4 shrink-0 text-xs text-muted-foreground">
                          {index + 1}.
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{client.name}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {client.documents}{' '}
                            {client.documents === 1 ? 'documento' : 'documentos'}
                            {client.missingCostLines > 0
                              ? ` · ${client.missingCostLines} sem custo`
                              : ''}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold">{formatBRLShort(client.revenue)}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {client.marginPercent == null
                            ? 'margem —'
                            : `margem ${client.marginPercent.toFixed(0)}%`}
                        </p>
                      </div>
                    </div>
                  ))}
                  <ListTail
                    total={report.clients.length}
                    shown={shownClients.length}
                    noun="cliente"
                  />
                </div>
              ) : (
                <AwaitingData hint="Nenhuma venda com cliente no período selecionado" />
              )
            ) : (
              <div className="space-y-2">
                {['Cliente A', 'Cliente B', 'Cliente C'].map((name, i) => (
                  <div key={name} className="flex items-center justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{name}</span>
                    <span className="text-sm">margem {50 - i * 8}%</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* A soma por item NÃO fecha com o total dos orçamentos — dizer por quê
          evita a conclusão de que um dos dois relatórios está quebrado. */}
      {allowed && hasItems && (
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <p className="text-[11px] leading-snug text-muted-foreground">{ITEMS_REVENUE_NOTE}</p>
          </CardContent>
        </Card>
      )}

      {!allowed && (
        <Card className="border-amber-300/60 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10">
          <CardContent className="flex flex-col items-center py-6 text-center">
            <h3 className="text-sm font-semibold">Relatório por item</h3>
            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
              Veja o que você mais vende, separado entre serviços e produtos, com a margem de cada
              item e de cada cliente. Disponível no plano PRO.
            </p>
            <Button3D color="amber" size="sm" className="mt-4" onClick={onUnlock}>
              <Crown className="mr-2 h-4 w-4" />
              Desbloquear com PRO
            </Button3D>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== peças =====

/**
 * Rótulo do eixo de categoria. Nome de item é TEXTO LIVRE e longo ("Instalação
 * de tomada 220V na sala"); sem cortar, ele estoura a faixa do eixo e o recharts
 * clipa o começo do nome — o mesmo modo de falha do "R$" cortado (#43), que não
 * aparece em `tsc` nem no jsdom porque o texto existe, só está fora da viewBox.
 * O limite vem de uma MEDIÇÃO (ver {@link CATEGORY_AXIS_WIDTH}).
 */
function shortLabel(name: string): string {
  const clean = String(name ?? '').trim();
  if (clean.length <= LABEL_MAX_CHARS + 1) return clean;
  return `${clean.slice(0, LABEL_MAX_CHARS)}…`;
}

function ItemRow({ item, index }: { item: ItemAggregate; index: number }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b py-2 last:border-0">
      <div className="flex min-w-0 items-center gap-2">
        <span className="w-4 shrink-0 text-xs text-muted-foreground">{index + 1}.</span>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-sm font-medium">{item.name}</p>
            {/* Selo CURTO ao lado de nome variável (#52): um rótulo longo não
                quebra a linha, ele come o nome. */}
            {item.productId === null && (
              <Badge variant="outline" className="shrink-0 text-[10px]">
                {ADHOC_ITEM_BADGE}
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {formatQty(item.quantity)}
            {item.unit ? ` ${item.unit}` : ''} · {item.lines}{' '}
            {item.lines === 1 ? 'linha' : 'linhas'}
            {item.missingCostLines > 0 ? ` · ${item.missingCostLines} sem custo` : ''}
          </p>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold">{formatBRLShort(item.revenue)}</p>
        <p className="text-[11px] text-muted-foreground">
          {item.marginPercent == null ? 'margem —' : `margem ${item.marginPercent.toFixed(0)}%`}
        </p>
      </div>
    </div>
  );
}

interface KpiCardProps {
  icon: React.ReactNode;
  tint: string;
  label: string;
  value: string;
  valueClass: string;
  hint: string;
  allowed: boolean;
  onUnlock: () => void;
}

/**
 * Recebe `allowed`/`onUnlock` em vez de um wrapper definido no corpo do render —
 * um componente criado a cada render tem identidade nova e remontaria a
 * subárvore inteira toda vez (mesmo motivo do `StockReport`).
 */
function KpiCard({ icon, tint, label, value, valueClass, hint, allowed, onUnlock }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-3">
        <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
          <div className="flex flex-col">
            <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full ${tint}`}>
              {icon}
            </div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`truncate text-base font-bold ${valueClass}`}>{value}</p>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground/70">{hint}</p>
          </div>
        </BlurOverlay>
      </CardContent>
    </Card>
  );
}
